import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addClient } from '../utils/apiService';
import { ramOptions, cpuOptions, diskOptions, osOptions, mariadbVersionOptions, apacheVersionOptions, nodejsVersionOptions } from '../data/predefinedValues';
import { getEmptyServer, getEmptyLocalModification, serverTypes } from '../utils/serverModel';
import ServerForm from '../components/ServerForm';
import TeleMaintenanceConfig from '../components/TeleMaintenanceConfig';

const AddClient = () => {
  const navigate = useNavigate();
  const [clientBase, setClientBase] = useState({
    name: '',
    description: '',
    connectionProcedure: '',
    remoteMaintenance: false,
    slackChannelId: '',
    telemaintenance: {
      enabled: false,
      recipientEmail: '',
      ccEmails: [],
      defaultAccount: '',
      templateType: 'generic',
      customTemplate: '',
      defaultTemplate: ''
    }
  });

  const [environments, setEnvironments] = useState({
    production: {
      enabled: false,
      patientPortalUrl: '',
      carePortalUrl: '',
      patientPortalUrlExternallyAccessible: true,
      carePortalUrlExternallyAccessible: true,
      checkGeneral: '',
      servers: [getEmptyServer()]
    },
    qualification: {
      enabled: false,
      patientPortalUrl: '',
      carePortalUrl: '',
      patientPortalUrlExternallyAccessible: true,
      carePortalUrlExternallyAccessible: true,
      checkGeneral: '',
      servers: [getEmptyServer()]
    }
  });

  const [showLocalModifications, setShowLocalModifications] = useState({});
  const [activeTab, setActiveTab] = useState('general');

  // Gestion des informations générales du client
  const handleBaseInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setClientBase({ 
      ...clientBase, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  // Gestion de la configuration télémaintenance
  const handleTeleMaintenanceChange = (teleMaintenanceConfig) => {
    setClientBase({
      ...clientBase,
      telemaintenance: teleMaintenanceConfig
    });
  };

  // Gestion des environnements
  const toggleEnvironment = (env) => {
    setEnvironments({
      ...environments,
      [env]: {
        ...environments[env],
        enabled: !environments[env].enabled
      }
    });
  };

  const handleEnvInputChange = (env, field, value) => {
    setEnvironments({
      ...environments,
      [env]: {
        ...environments[env],
        [field]: value
      }
    });
  };

  // Gestion des serveurs
  const handleServerChange = (env, index, e) => {
    const { name, value, type, checked } = e.target;
    const updatedServers = [...environments[env].servers];
    
    updatedServers[index] = {
      ...updatedServers[index],
      [name]: type === 'checkbox' ? checked : value
    };
    
    // Ajuster automatiquement les valeurs selon le type de serveur
    if (name === 'type') {
      const typeObj = serverTypes.find(t => t.value === value);
      if (typeObj) {
        updatedServers[index].color = typeObj.color;
      }
      
      // Logique d'ajustement selon le type (comme dans l'ancien formulaire)
      switch(value) {
        case 'Serveur applicatif':
          updatedServers[index].mariadbVersion = '';
          break;
        case 'Serveur base de données':
          updatedServers[index].gitMarshallerVersion = '';
          updatedServers[index].gitMarshallerInteropVersion = '';
          updatedServers[index].gitManagerFrontAppVersion = '';
          updatedServers[index].gitPatientAppVersion = '';
          updatedServers[index].apacheVersion = '';
          break;
        case 'Serveur interop':
          updatedServers[index].mariadbVersion = '';
          updatedServers[index].gitManagerFrontAppVersion = '';
          updatedServers[index].gitPatientAppVersion = '';
          updatedServers[index].apacheVersion = '';
          break;
        default:
          break;
      }
    }
    
    setEnvironments({
      ...environments,
      [env]: {
        ...environments[env],
        servers: updatedServers
      }
    });
  };

  const addServer = (env) => {
    setEnvironments({
      ...environments,
      [env]: {
        ...environments[env],
        servers: [...environments[env].servers, getEmptyServer()]
      }
    });
  };

  const removeServer = (env, index) => {
    const updatedServers = [...environments[env].servers];
    updatedServers.splice(index, 1);
    setEnvironments({
      ...environments,
      [env]: {
        ...environments[env],
        servers: updatedServers
      }
    });
  };

  // Gestion des modifications locales
  const addLocalModification = (env, serverIndex, component) => {
    const key = `${env}-${serverIndex}-${component}`;
    const updatedServers = [...environments[env].servers];
    
    if (!updatedServers[serverIndex].localModifications) {
      updatedServers[serverIndex].localModifications = {
        patientApp: [],
        managerFrontApp: [],
        marshaller: [],
        marshallerInterop: []
      };
    }
    
    updatedServers[serverIndex].localModifications[component].push(getEmptyLocalModification());
    
    setEnvironments({
      ...environments,
      [env]: {
        ...environments[env],
        servers: updatedServers
      }
    });
    
    setShowLocalModifications({ ...showLocalModifications, [key]: true });
  };

  const handleLocalModificationChange = (env, serverIndex, component, modIndex, field, value) => {
    const updatedServers = [...environments[env].servers];
    updatedServers[serverIndex].localModifications[component][modIndex][field] = value;
    
    setEnvironments({
      ...environments,
      [env]: {
        ...environments[env],
        servers: updatedServers
      }
    });
  };

  const removeLocalModification = (env, serverIndex, component, modIndex) => {
    const updatedServers = [...environments[env].servers];
    updatedServers[serverIndex].localModifications[component].splice(modIndex, 1);
    
    setEnvironments({
      ...environments,
      [env]: {
        ...environments[env],
        servers: updatedServers
      }
    });
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Vérifier qu'au moins un environnement est activé
    if (!environments.production.enabled && !environments.qualification.enabled) {
      alert('Veuillez activer au moins un environnement (Production ou Qualification)');
      return;
    }
    
    // Créer les objets clients pour chaque environnement activé
    const clientsToCreate = [];
    
    if (environments.production.enabled) {
      clientsToCreate.push({
        ...clientBase,
        name: clientBase.name,
        environment: 'Production',
        patientPortalUrl: environments.production.patientPortalUrl,
        carePortalUrl: environments.production.carePortalUrl,
        patientPortalUrlExternallyAccessible: environments.production.patientPortalUrlExternallyAccessible,
        carePortalUrlExternallyAccessible: environments.production.carePortalUrlExternallyAccessible,
        checkGeneral: {
          prod: environments.production.checkGeneral,
          qual: ''
        },
        servers: environments.production.servers,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
    
    if (environments.qualification.enabled) {
      clientsToCreate.push({
        ...clientBase,
        name: clientBase.name + (environments.production.enabled ? ' QUAL' : ''),
        environment: 'Qualification',
        patientPortalUrl: environments.qualification.patientPortalUrl,
        carePortalUrl: environments.qualification.carePortalUrl,
        patientPortalUrlExternallyAccessible: environments.qualification.patientPortalUrlExternallyAccessible,
        carePortalUrlExternallyAccessible: environments.qualification.carePortalUrlExternallyAccessible,
        checkGeneral: {
          prod: '',
          qual: environments.qualification.checkGeneral
        },
        servers: environments.qualification.servers,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
    
    // Ajouter les clients
    try {
      let firstClientId = null;
      
      for (const client of clientsToCreate) {
        const result = await addClient(client);
        if (result && result.id && !firstClientId) {
          firstClientId = result.id;
        }
      }
      
      // Naviguer vers le premier client créé ou la liste
      if (firstClientId) {
        navigate(`/client/${firstClientId}`);
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout du client:', error);
      alert('Une erreur est survenue lors de l\'ajout du client');
    }
  };

  // Rendu du formulaire de serveur
  const renderServerForm = (env, server, index) => {
    return (
      <ServerForm
        key={`${env}-${index}`}
        server={server}
        index={index}
        env={env}
        onChange={handleServerChange}
        onAddLocalModification={addLocalModification}
        onLocalModificationChange={handleLocalModificationChange}
        onRemoveLocalModification={removeLocalModification}
        showLocalModifications={showLocalModifications}
        onRemove={removeServer}
        canRemove={environments[env].servers.length > 1}
      />
    );
  };

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6">Ajouter un nouveau client</h1>
      
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Tabs de navigation */}
        <div className="flex border-b">
          <button
            type="button"
            onClick={() => setActiveTab('general')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'general' 
                ? 'border-b-2 border-blue-500 text-blue-600' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Informations générales
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('production')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'production' 
                ? 'border-b-2 border-red-500 text-red-600' 
                : 'text-gray-600 hover:text-gray-800'
            } ${!environments.production.enabled && 'opacity-50'}`}
            disabled={!environments.production.enabled}
          >
            Production {environments.production.enabled && '✓'}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('qualification')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'qualification' 
                ? 'border-b-2 border-orange-500 text-orange-600' 
                : 'text-gray-600 hover:text-gray-800'
            } ${!environments.qualification.enabled && 'opacity-50'}`}
            disabled={!environments.qualification.enabled}
          >
            Qualification {environments.qualification.enabled && '✓'}
          </button>
        </div>
        
        <div className="p-6">
          {/* Tab Informations générales */}
          {activeTab === 'general' && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Informations générales du client</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-gray-700 mb-2" htmlFor="name">
                    Nom du client *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="w-full p-3 border border-gray-300 rounded"
                    value={clientBase.name}
                    onChange={handleBaseInputChange}
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Ne pas inclure "QUAL" ou "PROD" dans le nom
                  </p>
                </div>
                
                <div>
                  <label className="block text-gray-700 mb-2" htmlFor="slackChannelId">
                    Canal Slack
                  </label>
                  <input
                    type="text"
                    id="slackChannelId"
                    name="slackChannelId"
                    className="w-full p-3 border border-gray-300 rounded"
                    value={clientBase.slackChannelId}
                    onChange={handleBaseInputChange}
                    placeholder="nom-du-canal"
                  />
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-gray-700 mb-2" htmlFor="description">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows="3"
                  className="w-full p-3 border border-gray-300 rounded"
                  value={clientBase.description}
                  onChange={handleBaseInputChange}
                ></textarea>
              </div>
              
              <div className="mb-6">
                <label className="block text-gray-700 mb-2" htmlFor="connectionProcedure">
                  Procédure de connexion
                </label>
                <textarea
                  id="connectionProcedure"
                  name="connectionProcedure"
                  rows="4"
                  className="w-full p-3 border border-gray-300 rounded"
                  value={clientBase.connectionProcedure}
                  onChange={handleBaseInputChange}
                ></textarea>
              </div>
              
              <div className="mb-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="remoteMaintenance"
                    name="remoteMaintenance"
                    className="w-5 h-5 mr-2 border border-gray-300 rounded"
                    checked={clientBase.remoteMaintenance}
                    onChange={handleBaseInputChange}
                  />
                  <label className="text-gray-700" htmlFor="remoteMaintenance">
                    Télémaintenance à débloquer pour accès aux serveurs
                  </label>
                </div>
              </div>
              
              {/* Sélection des environnements */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-4">Environnements disponibles</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-white rounded border">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="enableProduction"
                        className="w-5 h-5 mr-3"
                        checked={environments.production.enabled}
                        onChange={() => toggleEnvironment('production')}
                      />
                      <label htmlFor="enableProduction" className="cursor-pointer">
                        <span className="font-medium text-red-600">Production</span>
                        <p className="text-sm text-gray-500">Activer l'environnement de production</p>
                      </label>
                    </div>
                    {environments.production.enabled && (
                      <button
                        type="button"
                        onClick={() => setActiveTab('production')}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        Configurer →
                      </button>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-white rounded border">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="enableQualification"
                        className="w-5 h-5 mr-3"
                        checked={environments.qualification.enabled}
                        onChange={() => toggleEnvironment('qualification')}
                      />
                      <label htmlFor="enableQualification" className="cursor-pointer">
                        <span className="font-medium text-orange-600">Qualification</span>
                        <p className="text-sm text-gray-500">Activer l'environnement de qualification</p>
                      </label>
                    </div>
                    {environments.qualification.enabled && (
                      <button
                        type="button"
                        onClick={() => setActiveTab('qualification')}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        Configurer →
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Configuration Télémaintenance */}
              <div className="mt-8">
                <TeleMaintenanceConfig
                  value={clientBase.telemaintenance}
                  remoteMaintenance={clientBase.remoteMaintenance}
                  onChange={handleTeleMaintenanceChange}
                  onRemoteMaintenanceChange={(enabled) => setClientBase({...clientBase, remoteMaintenance: enabled})}
                />
              </div>
            </div>
          )}
          
          {/* Tab Production */}
          {activeTab === 'production' && environments.production.enabled && (
            <div>
              <h2 className="text-xl font-semibold mb-4 text-red-600">Configuration Production</h2>
              
              <div className="mb-6">
                <h3 className="font-semibold mb-3">URLs Production</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 mb-2">URL du portail patient</label>
                    <input
                      type="url"
                      className="w-full p-3 border border-gray-300 rounded"
                      value={environments.production.patientPortalUrl}
                      onChange={(e) => handleEnvInputChange('production', 'patientPortalUrl', e.target.value)}
                      placeholder="https://patient.example.com"
                    />
                    <div className="flex items-center mt-2">
                      <input
                        type="checkbox"
                        id="prod-patient-external"
                        className="w-4 h-4 mr-2 border border-gray-300 rounded"
                        checked={environments.production.patientPortalUrlExternallyAccessible}
                        onChange={(e) => handleEnvInputChange('production', 'patientPortalUrlExternallyAccessible', e.target.checked)}
                      />
                      <label className="text-sm text-gray-600" htmlFor="prod-patient-external">
                        Accessible depuis l'extérieur
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-2">URL du portail soignant</label>
                    <input
                      type="url"
                      className="w-full p-3 border border-gray-300 rounded"
                      value={environments.production.carePortalUrl}
                      onChange={(e) => handleEnvInputChange('production', 'carePortalUrl', e.target.value)}
                      placeholder="https://care.example.com"
                    />
                    <div className="flex items-center mt-2">
                      <input
                        type="checkbox"
                        id="prod-care-external"
                        className="w-4 h-4 mr-2 border border-gray-300 rounded"
                        checked={environments.production.carePortalUrlExternallyAccessible}
                        onChange={(e) => handleEnvInputChange('production', 'carePortalUrlExternallyAccessible', e.target.checked)}
                      />
                      <label className="text-sm text-gray-600" htmlFor="prod-care-external">
                        Accessible depuis l'extérieur
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-gray-700 mb-2">Check général Production</label>
                <input
                  type="text"
                  className="w-full p-3 border border-gray-300 rounded"
                  value={environments.production.checkGeneral}
                  onChange={(e) => handleEnvInputChange('production', 'checkGeneral', e.target.value)}
                  placeholder="Dernière vérification le JJ/MM/AAAA - Status"
                />
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold">Serveurs Production</h3>
                  <button
                    type="button"
                    onClick={() => addServer('production')}
                    className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                  >
                    + Ajouter un serveur
                  </button>
                </div>
                
                {environments.production.servers.map((server, index) => 
                  renderServerForm('production', server, index)
                )}
              </div>
            </div>
          )}
          
          {/* Tab Qualification */}
          {activeTab === 'qualification' && environments.qualification.enabled && (
            <div>
              <h2 className="text-xl font-semibold mb-4 text-orange-600">Configuration Qualification</h2>
              
              <div className="mb-6">
                <h3 className="font-semibold mb-3">URLs Qualification</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 mb-2">URL du portail patient</label>
                    <input
                      type="url"
                      className="w-full p-3 border border-gray-300 rounded"
                      value={environments.qualification.patientPortalUrl}
                      onChange={(e) => handleEnvInputChange('qualification', 'patientPortalUrl', e.target.value)}
                      placeholder="https://qual-patient.example.com"
                    />
                    <div className="flex items-center mt-2">
                      <input
                        type="checkbox"
                        id="qual-patient-external"
                        className="w-4 h-4 mr-2 border border-gray-300 rounded"
                        checked={environments.qualification.patientPortalUrlExternallyAccessible}
                        onChange={(e) => handleEnvInputChange('qualification', 'patientPortalUrlExternallyAccessible', e.target.checked)}
                      />
                      <label className="text-sm text-gray-600" htmlFor="qual-patient-external">
                        Accessible depuis l'extérieur
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-2">URL du portail soignant</label>
                    <input
                      type="url"
                      className="w-full p-3 border border-gray-300 rounded"
                      value={environments.qualification.carePortalUrl}
                      onChange={(e) => handleEnvInputChange('qualification', 'carePortalUrl', e.target.value)}
                      placeholder="https://qual-care.example.com"
                    />
                    <div className="flex items-center mt-2">
                      <input
                        type="checkbox"
                        id="qual-care-external"
                        className="w-4 h-4 mr-2 border border-gray-300 rounded"
                        checked={environments.qualification.carePortalUrlExternallyAccessible}
                        onChange={(e) => handleEnvInputChange('qualification', 'carePortalUrlExternallyAccessible', e.target.checked)}
                      />
                      <label className="text-sm text-gray-600" htmlFor="qual-care-external">
                        Accessible depuis l'extérieur
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-gray-700 mb-2">Check général Qualification</label>
                <input
                  type="text"
                  className="w-full p-3 border border-gray-300 rounded"
                  value={environments.qualification.checkGeneral}
                  onChange={(e) => handleEnvInputChange('qualification', 'checkGeneral', e.target.value)}
                  placeholder="Dernière vérification le JJ/MM/AAAA - Status"
                />
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold">Serveurs Qualification</h3>
                  <button
                    type="button"
                    onClick={() => addServer('qualification')}
                    className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                  >
                    + Ajouter un serveur
                  </button>
                </div>
                
                {environments.qualification.servers.map((server, index) => 
                  renderServerForm('qualification', server, index)
                )}
              </div>
            </div>
          )}
          
          {/* Boutons d'action */}
          <div className="mt-6 pt-6 border-t flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Créer le client
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddClient;